import { api } from "@api/api";
import { reactQueryUtils } from "@lib/react-query";
import { resetStore, useAppDispatch } from "@store";
import { onAuthStateChanged } from "firebase/auth";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { signOut, useAuthStoreShallow } from "@/features/authentication";
import { auth } from "@/lib/firebase";

// Define the shape of our auth context
interface AuthContextType {
  loading: boolean;
  isLoggingOut: boolean;
  logout: () => Promise<void>;
}

// Create the auth context
const AuthContext = createContext<AuthContextType | null>(null);

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Props for the AuthProvider component
interface AuthProviderProps {
  children: ReactNode;
}

// AuthProvider component
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const dispatch = useAppDispatch();
  const { setUser, setAuthClaims } = useAuthStoreShallow((s) => ({
    setUser: s.setUser,
    setAuthClaims: s.setAuthClaims,
  }));

  // Logout function
  const logout = async () => {
    setIsLoggingOut(true);
    try {
      // Clear user state and claims first
      setUser(null);
      setAuthClaims(null);
      // Then sign out from Firebase
      await signOut();
      setTimeout(() => {
        reactQueryUtils.clear();
        api.util.resetApiState();
        dispatch(resetStore());
      }, 500);
    } catch {
    }
    setIsLoggingOut(false);
  };
  // Set up auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        const { claims } = await user.getIdTokenResult();
        setAuthClaims(claims);
      }
      setLoading(false);
    });

    // Clean up subscription
    return unsubscribe;
  }, [setUser, setAuthClaims]);

  // Create value object for the context
  const value = {
    loading,
    isLoggingOut,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
